import { QueryClient } from '@tanstack/react-query';
import { ApiRequestError } from './apiClient';

/**
 * Shared cache configuration (spec section 85).
 *
 * Content changes rarely, so a generous `staleTime` keeps navigation instant.
 * 4xx responses are never retried — a 403 will not become a 200 on the third go.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query key factory. Centralising keys means an invalidation after a mutation
 * cannot silently miss a cache entry because of a typo.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  courses: {
    all: ['courses'] as const,
    list: (params?: unknown) => ['courses', 'list', params ?? {}] as const,
    detail: (idOrSlug: string) => ['courses', 'detail', idOrSlug] as const,
  },
  modules: {
    detail: (id: string) => ['modules', 'detail', id] as const,
  },
  lessons: {
    detail: (id: string) => ['lessons', 'detail', id] as const,
    resources: (id: string) => ['lessons', id, 'resources'] as const,
    exercises: (id: string) => ['lessons', id, 'exercises'] as const,
    quiz: (id: string) => ['lessons', id, 'quiz'] as const,
    skipEligibility: (id: string) => ['lessons', id, 'skip-eligibility'] as const,
  },
  learningPaths: {
    all: ['learning-paths'] as const,
    detail: (id: string) => ['learning-paths', id] as const,
  },
  progress: {
    mine: ['progress', 'mine'] as const,
    course: (courseId: string) => ['progress', 'course', courseId] as const,
    history: (params?: unknown) => ['progress', 'history', params ?? {}] as const,
  },
  dashboard: {
    intern: ['dashboard', 'intern'] as const,
    admin: ['dashboard', 'admin'] as const,
    accountant: ['dashboard', 'accountant'] as const,
  },
  exercises: {
    list: (params?: unknown) => ['exercises', 'list', params ?? {}] as const,
    detail: (id: string) => ['exercises', 'detail', id] as const,
  },
  submissions: {
    mine: (params?: unknown) => ['submissions', 'mine', params ?? {}] as const,
    list: (params?: unknown) => ['submissions', 'list', params ?? {}] as const,
    queue: (params?: unknown) => ['submissions', 'queue', params ?? {}] as const,
    detail: (id: string) => ['submissions', 'detail', id] as const,
  },
  quizzes: {
    attempt: (attemptId: string) => ['quizzes', 'attempt', attemptId] as const,
    admin: (id: string) => ['quizzes', 'admin', id] as const,
  },
  skip: {
    requests: (params?: unknown) => ['skip', 'requests', params ?? {}] as const,
    mine: ['skip', 'mine'] as const,
    assessment: (id: string) => ['skip', 'assessment', id] as const,
    adminAssessment: (id: string) => ['skip', 'assessment', 'admin', id] as const,
    report: (params?: unknown) => ['skip', 'report', params ?? {}] as const,
  },
  assessments: {
    all: ['assessments'] as const,
    /** The admin list, which is filtered differently from the learner's. */
    list: (params?: unknown) => ['assessments', 'list', params ?? {}] as const,
    detail: (id: string) => ['assessments', id] as const,
    admin: (id: string) => ['assessments', 'admin', id] as const,
  },
  recognition: {
    readiness: (userId?: string) => ['recognition', 'readiness', userId ?? 'me'] as const,
    achievements: ['recognition', 'achievements'] as const,
    myAchievements: ['recognition', 'achievements', 'mine'] as const,
    certificates: ['recognition', 'certificates', 'mine'] as const,
  },
  interns: {
    list: (params?: unknown) => ['interns', 'list', params ?? {}] as const,
    detail: (userId: string) => ['interns', 'detail', userId] as const,
    skills: (userId: string) => ['interns', userId, 'skills'] as const,
    history: (userId: string, params?: unknown) => ['interns', userId, 'history', params ?? {}] as const,
  },
  users: {
    list: (params?: unknown) => ['users', 'list', params ?? {}] as const,
  },
  skills: {
    all: ['skills'] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (params?: unknown) => ['projects', 'list', params ?? {}] as const,
    mine: ['projects', 'mine'] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    members: (id: string) => ['projects', id, 'members'] as const,
    board: (id: string) => ['projects', id, 'board'] as const,
    tasks: (id: string, params?: unknown) => ['projects', id, 'tasks', params ?? {}] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    mine: ['tasks', 'mine'] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
    /** The mentor's queue of task submissions awaiting a decision. */
    reviewQueue: (params?: unknown) => ['tasks', 'submissions', 'queue', params ?? {}] as const,
    submission: (id: string) => ['tasks', 'submissions', 'detail', id] as const,
  },
  clients: {
    list: (params?: unknown) => ['clients', 'list', params ?? {}] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
  },
  finance: {
    expenses: (params?: unknown) => ['finance', 'expenses', params ?? {}] as const,
    revenues: (params?: unknown) => ['finance', 'revenues', params ?? {}] as const,
    invoices: (params?: unknown) => ['finance', 'invoices', params ?? {}] as const,
    invoice: (id: string) => ['finance', 'invoices', id] as const,
    payments: (params?: unknown) => ['finance', 'payments', params ?? {}] as const,
    profitability: ['finance', 'profitability'] as const,
  },
  admin: {
    reports: ['admin', 'reports'] as const,
    report: (type: string, params?: unknown) => ['admin', 'reports', type, params ?? {}] as const,
    analyticsInterns: (params?: unknown) => ['admin', 'analytics', 'interns', params ?? {}] as const,
    analyticsCourses: (params?: unknown) => ['admin', 'analytics', 'courses', params ?? {}] as const,
    activity: (params?: unknown) => ['admin', 'activity', params ?? {}] as const,
    auditLogs: (params?: unknown) => ['admin', 'audit-logs', params ?? {}] as const,
    settings: ['admin', 'settings'] as const,
  },
  notifications: {
    list: (params?: unknown) => ['notifications', 'list', params ?? {}] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
} as const;
